/**
 * Historico dos ultimos eventos de um provedor de webhook.
 *
 * Decisoes nao obvias:
 *  - E uma LISTA de linhas compactas, nao tabela: mora dentro de um cartao de
 *    provedor que divide a grade com outros. Cada linha tem quando, estado, o
 *    que era e de quem -- nessa ordem, porque a pergunta de quem abre e "esta
 *    chegando? esta entrando?".
 *  - Mostra 5 e cresce de 10 em 10 ("ver mais"): o servidor manda ate 50 e
 *    uma empresa grande recebe centenas por dia; renderizar tudo de uma vez
 *    faria o cartao ter a altura da pagina.
 *  - O filtro por estado tem contador, para "3 rejeitados" saltar aos olhos
 *    antes de qualquer clique. Rejeitado tem o MOTIVO por escrito: um 401 sem
 *    explicacao e exatamente a falha silenciosa que este bloco existe para
 *    tirar do silencio.
 *  - Telefone mascarado, dinheiro por `formatBRLFromCents`, nome de produto
 *    do catalogo. Evento de teste leva selo proprio e nunca conta como lead.
 */

import { useState } from 'react';
import { Badge, Button, FilterChips, Icon, type ChipOption } from '../../../components/ui';
import { formatBRLFromCents, formatCount, formatDateTime, maskPhoneDisplay } from '../../../lib/format';
import type { WebhookEventLog, WebhookEventStatus } from '../types';
import { EVENT_LABEL, EVENT_STATUS, REJECT_REASON_LABEL, descricaoHttp } from '../vocabulario';

type Filtro = 'todos' | WebhookEventStatus;

const PAGINA_INICIAL = 5;
const PASSO = 10;

function tituloDe(evento: WebhookEventLog): string {
  if (evento.type !== null) {
    return EVENT_LABEL[evento.type];
  }
  return evento.reason === null ? 'Evento não identificado' : REJECT_REASON_LABEL[evento.reason].titulo;
}

function Linha({ evento }: { readonly evento: WebhookEventLog }): JSX.Element {
  const estado = EVENT_STATUS[evento.status];
  const partes = [
    evento.productName,
    evento.amountCents === null ? null : formatBRLFromCents(evento.amountCents),
    evento.phoneE164 === null ? null : maskPhoneDisplay(evento.phoneE164),
    evento.source === null ? null : `via ${evento.source}`,
  ].filter((p): p is string => p !== null);
  const motivo = evento.status === 'rejected' && evento.reason !== null ? REJECT_REASON_LABEL[evento.reason] : null;

  return (
    <li className="flex flex-col gap-1 py-2.5 text-xs">
      <div className="flex flex-wrap items-center gap-2">
        <time dateTime={evento.receivedAt} className="tabular-nums text-ink-subtle">
          {formatDateTime(evento.receivedAt)}
        </time>
        <Badge tone={estado.tone} dot>
          {estado.label}
        </Badge>
        <span className="font-mono text-[10px] text-ink-subtle" title={`Resposta HTTP ${evento.httpStatus}: ${descricaoHttp(evento.httpStatus)}`}>
          {evento.httpStatus} {descricaoHttp(evento.httpStatus)}
        </span>
        {evento.test ? (
          <Badge tone="info" icon="raio">
            Teste
          </Badge>
        ) : null}
      </div>
      <p className={`leading-snug ${evento.status === 'rejected' ? 'font-medium text-status-critical' : 'text-ink'}`}>{tituloDe(evento)}</p>
      {partes.length > 0 ? <p className="text-ink-muted">{partes.join(' · ')}</p> : null}
      {motivo !== null ? <p className="text-ink-subtle">{motivo.oQueFazer}</p> : null}
    </li>
  );
}

export function HistoricoDeEventos({
  eventos,
  eventsLast7Days,
  rejectedLast7Days,
  connected,
}: {
  readonly eventos: readonly WebhookEventLog[];
  readonly eventsLast7Days: number;
  readonly rejectedLast7Days: number;
  readonly connected: boolean;
}): JSX.Element {
  const [filtro, setFiltro] = useState<Filtro>('todos');
  const [limite, setLimite] = useState(PAGINA_INICIAL);

  const contar = (status: WebhookEventStatus): number => eventos.filter((e) => e.status === status).length;
  const opcoes: readonly ChipOption<Filtro>[] = [
    { value: 'todos', label: 'Todos', count: formatCount(eventos.length) },
    { value: 'accepted', label: EVENT_STATUS.accepted.label, count: formatCount(contar('accepted')) },
    { value: 'rejected', label: EVENT_STATUS.rejected.label, count: formatCount(contar('rejected')) },
    { value: 'duplicate', label: EVENT_STATUS.duplicate.label, count: formatCount(contar('duplicate')) },
  ];

  const filtrados = filtro === 'todos' ? eventos : eventos.filter((e) => e.status === filtro);
  const visiveis = filtrados.slice(0, limite);
  const restantes = filtrados.length - visiveis.length;

  const trocarFiltro = (v: Filtro): void => {
    setFiltro(v);
    setLimite(PAGINA_INICIAL);
  };

  return (
    <section aria-label="Últimos eventos" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">Últimos eventos</h4>
        {connected ? (
          <p className="text-xs text-ink-subtle" title="Contagem dos últimos 7 dias, independente da janela do cabeçalho">
            7 dias: <span className="font-medium tabular-nums text-ink">{formatCount(eventsLast7Days)}</span>
            {rejectedLast7Days > 0 ? (
              <>
                {' '}
                · <span className="font-medium tabular-nums text-status-critical">{formatCount(rejectedLast7Days)}</span> rejeitados
              </>
            ) : null}
          </p>
        ) : null}
      </div>

      {eventos.length === 0 ? (
        <p className="flex items-start gap-2 rounded-lg border border-dashed border-edge px-3 py-3 text-xs text-ink-muted">
          <Icon name="info" size="xs" className="mt-0.5 shrink-0" />
          <span>
            {connected
              ? 'Nenhum evento chegou ainda. Faça um checkout de teste na plataforma: o evento aparece aqui em segundos e, 30 minutos sem pagar, entra na régua.'
              : 'Sem eventos: esta plataforma ainda não envia para esta URL. Cole a URL acima no painel dela para começar.'}
          </span>
        </p>
      ) : (
        <>
          <FilterChips options={opcoes} value={filtro} onChange={trocarFiltro} label="Filtrar eventos por estado" />
          {visiveis.length === 0 ? (
            <p className="rounded-lg border border-dashed border-edge px-3 py-3 text-xs text-ink-muted">Nenhum evento neste estado entre os últimos recebidos.</p>
          ) : (
            <ul className="divide-y divide-line rounded-lg border border-line bg-surface px-3">
              {visiveis.map((e) => (
                <Linha key={e.id} evento={e} />
              ))}
            </ul>
          )}
          {restantes > 0 ? (
            <div>
              <Button variant="ghost" size="sm" icon="chevron" onClick={() => setLimite((l) => l + PASSO)}>
                Ver mais {formatCount(Math.min(PASSO, restantes))} de {formatCount(restantes)}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}
